import actionIcon from 'assets/icons/PPSIcons/actionIcon.svg';
import deleteIcon from 'assets/icons/PPSIcons/deleteIcon.svg';
import editIcon from 'assets/icons/PPSIcons/editIcon.svg';
import searchIcon from 'assets/icons/PPSIcons/searchIcon.svg';
import { ClearTime } from 'Interfaces/ReferenceInterfaces/ClearTimeInterface';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useRef } from 'react';
import { commaSeparatedNumbers } from '../../../../utils/commaSeparatedNumbers';
import classes from './clearTimeTable.module.scss';
import { useClearTimeTable } from './useClearTimeTable';
import AddRowModalClearTime from './utils/AddRowModalClearTime';
import DeleteConfirmationModalClearTime from './utils/DeleteConfirmationModalClearTime';

interface ClearTimeTableProps {
  showAddButton?: boolean;
  showActionColumn?: boolean;
  showAllRows?: boolean;
}

const ClearTimeTable = observer(
  ({ showAddButton = true, showActionColumn = true, showAllRows = true }: ClearTimeTableProps) => {
    const pageSize = showAllRows ? 10000 : 5;
    const {
      clearTimeData,
      isLoading,
      pagination,
      itemMenu,
      selectedRow,
      showAddModal,
      showDeleteModal,
      setSelectedRow,
      setShowAddModal,
      setShowDeleteModal,
      handleAddRow,
      handleDeleteRow,
      prepareFormData,
      roomOptions,
      cleanTypeOptions,
      outgoingJobTypeOptions,
      incomingJobTypeOptions,
      onPageChange,
      searchQuery,
      handleSearchChange,
      handleExportToExcel,
      handleModalClose
    } = useClearTimeTable(pageSize);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const menuItems = [
      {
        label: 'Edit Row',
        icon: <img src={editIcon} alt="Edit Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowAddModal()
      },
      {
        label: 'Delete Row',
        icon: <img src={deleteIcon} alt="Delete Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowDeleteModal(true)
      }
    ];

    const actionBodyTemplate = (rowData: ClearTime) => {
      return (
        <Button
          icon={<img src={actionIcon} alt="Action Icon" style={{ marginRight: '0.5rem' }} />}
          className={`p-button-rounded p-button-text p-button-sm ${classes.menuButton}`}
          onClick={e => {
            setSelectedRow(rowData);
            itemMenu.current?.toggle(e);
          }}
          aria-controls="popup_menu"
          aria-haspopup
        />
      );
    };

    const roomTemplate = (rowData: ClearTime) => {
      return rowData.Room_Type?.description;
    };

    const cleanTypeTemplate = (rowData: ClearTime) => {
      return rowData.CleanType.description;
    };

    const outgoingJobTypeTemplate = (rowData: ClearTime) => {
      return rowData.outgoing_job_type.description;
    };

    const incomingJobTypeTemplate = (rowData: ClearTime) => {
      return rowData.incoming_job_type.description;
    };

    const clearTimeBodyTemplate = (rowData: ClearTime) => {
      return commaSeparatedNumbers(rowData.clear_time_minutes);
    };

    if (isLoading && !clearTimeData.length) {
      return (
        <div className={classes.loadingContainer}>
          <ProgressSpinner className={classes.spinner} />
        </div>
      );
    }

    return (
      <div className={classes.clearTimeTable}>
        <div className={classes.tableHeader}>
          <h2 className={classes.sectionTitle}>Clear Time Configuration</h2>
          <div className={classes.headerActions}>
            <div className={classes.searchContainer}>
              <span className={classes.searchIconSection}>
                <img src={searchIcon} alt="Search Icon" style={{ marginRight: '0.5rem' }} />
              </span>
              <InputText
                ref={searchInputRef}
                value={searchQuery}
                className={classes.searchInput}
                placeholder="Search..."
                onChange={e => handleSearchChange(e.target.value)}
              />
            </div>

            <Button
              icon="pi pi-download"
              className={`${classes.addButton} p-button-icon-only`}
              onClick={handleExportToExcel}
              tooltip="Export to Excel"
              tooltipOptions={{ position: 'bottom' }}
              style={{ color: 'white !important', fontSize: '1rem' }}
            />

            {showAddButton && (
              <Button
                label="Add New Row"
                icon="pi pi-plus"
                className={classes.addButton}
                onClick={() => {
                  setSelectedRow(null);
                  setShowAddModal();
                }}
              />
            )}
          </div>
        </div>

        <DataTable
          value={clearTimeData}
          showGridlines
          stripedRows
          emptyMessage="No records found"
          className={classes.dataTable}
          loading={isLoading}
          paginator={!showAllRows}
          rows={showAllRows ? clearTimeData.length : 100}
          totalRecords={pagination.totalItems}
          lazy
          first={(pagination.currentPage - 1) * 100}
          onPage={onPageChange}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        >
          <Column field="RoomType" header="Room Type" body={roomTemplate} />
          <Column
            field="outgoing_job_type"
            header="Outgoing Job Type"
            body={outgoingJobTypeTemplate}
          />
          <Column
            field="incoming_job_type"
            header="Incoming Job Type"
            body={incomingJobTypeTemplate}
          />
          <Column field="CleanType" header="Clean Type" body={cleanTypeTemplate} />
          <Column
            field="clear_time_minutes"
            header="Clear Time (mins)"
            body={clearTimeBodyTemplate}
          />
          {showActionColumn && (
            <Column
              body={actionBodyTemplate}
              style={{ width: '5rem', textAlign: 'center' }}
              headerStyle={{ width: '5rem' }}
            />
          )}
        </DataTable>

        <Menu
          model={menuItems}
          className={classes.actionMenu}
          popup
          ref={itemMenu}
          id="popup_menu"
        />

        <AddRowModalClearTime
          visible={showAddModal}
          onHide={handleModalClose}
          onSubmit={handleAddRow}
          initialValues={selectedRow ? prepareFormData(selectedRow) : undefined}
          isEdit={!!selectedRow}
          roomOptions={roomOptions}
          cleanTypeOptions={cleanTypeOptions}
          outgoingJobTypeOptions={outgoingJobTypeOptions}
          incomingJobTypeOptions={incomingJobTypeOptions}
        />

        <DeleteConfirmationModalClearTime
          visible={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedRow(null);
          }}
          onConfirm={handleDeleteRow}
        />
      </div>
    );
  }
);

export default ClearTimeTable;
